/**
 * Map crawl-extracted role and license strings to system RBAC values.
 *
 * Mirrors the backend logic in services/admin/crawl_staff_mapper.py so
 * the CrawlEditMode UI can display proper RBAC role dropdowns and
 * canonical license type values even before the data is sent to the backend.
 */
import type { RoleOption, CrawledStaffMember } from '../types';

/**
 * Map of keywords (lowercase) to system role values.
 * Order matters: first match wins, so more specific patterns come first.
 * Mirrors backend _CRAWL_ROLE_MAP in crawl_staff_mapper.py.
 */
const CRAWL_ROLE_MAP: [string, string][] = [
  ['medical assistant', 'medical_assistant'],
  ['intake coordinator', 'intake_coordinator'],
  ['billing', 'billing_specialist'],
  ['receptionist', 'admin'],
  ['office manager', 'office_manager'],
];

/**
 * License / credential alias map (lowercase → canonical value).
 * Mirrors backend _LICENSE_ALIAS in crawl_staff_mapper.py.
 */
const LICENSE_ALIAS: Record<string, string> = {
  'licensed clinical psychologist': 'LCP',
  'lcp': 'LCP',
  'licensed professional counselor': 'LPC',
  'licensed clinical social worker': 'LCSW',
  'licensed marriage and family therapist': 'LMFT',
  'licensed professional clinical counselor': 'LPCC',
  'pmhnp-bc': 'PMHNP',
  'pmhnp': 'PMHNP',
  'aprn': 'NP',
  'arnp': 'NP',
  'np': 'NP',
  'md': 'MD',
  'm.d.': 'MD',
  'do': 'DO',
  'd.o.': 'DO',
  'pa': 'PA',
  'pa-c': 'PA',
  'lac': 'LAC',
  'cac': 'CAC',
  'cma': 'CMA',
  'pre-licensed': 'Pre-Licensed',
  'associate': 'Pre-Licensed',
  'student therapist': 'Student Therapist',
  'intern': 'Pre-Licensed',
  'rpt': 'RPT',
};

/**
 * Degree alias map (lowercase → canonical value).
 * Mirrors backend _DEGREE_ALIAS in crawl_staff_mapper.py.
 */
const DEGREE_ALIAS: Record<string, string> = {
  'doctor of psychology': 'PsyD',
  'psyd': 'PsyD',
  'psy.d': 'PsyD',
  'psy.d.': 'PsyD',
  'phd': 'PhD',
  'ph.d': 'PhD',
  'ph.d.': 'PhD',
  'md': 'MD',
  'm.d.': 'MD',
  'do': 'DO',
  'd.o.': 'DO',
  'msw': 'MSW',
  'm.s.w.': 'MSW',
  'ma': 'MA',
  'm.a.': 'MA',
  'ms': 'MS',
  'm.s.': 'MS',
  'msn': 'MSN',
  'm.s.n.': 'MSN',
  'med': 'MEd',
  'm.ed.': 'MEd',
  'mba': 'MBA',
  'm.b.a.': 'MBA',
  'edd': 'EdD',
  'ed.d.': 'EdD',
  'ed.d': 'EdD',
  'dnp': 'DNP',
  'd.n.p.': 'DNP',
};

/**
 * Infer the RBAC role value from a crawl member's role/title/license fields.
 *
 * Returns a value from ROLE_OPTIONS (e.g. "provider", "clinical_director")
 * or "provider" as the safe default.
 */
export function inferRole(
  member: CrawledStaffMember,
  roleOptions: RoleOption[],
): string {
  // If the role is already a valid RBAC value, keep it.
  const validValues = new Set(roleOptions.map((r) => r.value));
  if (member.role && validValues.has(member.role)) {
    return member.role;
  }

  // Combine role + title + license_type for keyword matching.
  // license_type is now string[] so join with spaces for proper matching.
  const licenseStr = Array.isArray(member.license_type)
    ? member.license_type.join(' ')
    : (member.license_type ?? '');
  const combined = [member.role ?? '', member.title ?? '', licenseStr]
    .join(' ')
    .toLowerCase();

  for (const [keyword, role] of CRAWL_ROLE_MAP) {
    if (combined.includes(keyword)) {
      return role;
    }
  }

  // Fallback: if they have a license, they're a provider
  if (Array.isArray(member.license_type) ? member.license_type.length > 0 : member.license_type) return 'provider';

  return 'provider'; // safe default for clinical staff
}

/**
 * Normalise a crawl-extracted license string to a canonical LICENSE_TYPES value.
 *
 * Returns the canonical value (e.g. "LCSW", "NP") or empty string if
 * no match is found.
 */
export function normaliseLicense(
  raw: string | null,
  licenseTypes: string[],
): string {
  if (!raw) return '';
  const cleaned = raw.trim();
  const low = cleaned.toLowerCase();

  // Exact match against canonical list (case-insensitive)
  const licenseSet = new Set(licenseTypes.map((lt) => lt.toLowerCase()));
  if (licenseSet.has(low)) {
    return licenseTypes.find((lt) => lt.toLowerCase() === low) ?? cleaned;
  }

  // Alias lookup
  if (low in LICENSE_ALIAS) {
    return LICENSE_ALIAS[low];
  }

  // Prefix matching: "LCSW-C" → "LCSW", "LPC-S" → "LPC"
  const sorted = [...licenseTypes].sort((a, b) => b.length - a.length);
  for (const lt of sorted) {
    const ltLow = lt.toLowerCase();
    if (low.startsWith(ltLow)) {
      if (low.length === ltLow.length || !/[a-z]/i.test(low[ltLow.length])) {
        return lt;
      }
    }
  }

  return '';
}

/**
 * Normalise a crawl-extracted degree string to a canonical DEGREE_TYPES value.
 *
 * Returns the canonical value (e.g. "PhD", "MSW") or empty string if
 * no match is found.
 */
export function normaliseDegree(
  raw: string | null,
  degreeTypes: string[],
): string {
  if (!raw) return '';
  const cleaned = raw.trim();
  const low = cleaned.toLowerCase();

  // Exact match against canonical list (case-insensitive)
  const degreeSet = new Set(degreeTypes.map((d) => d.toLowerCase()));
  if (degreeSet.has(low)) {
    return degreeTypes.find((d) => d.toLowerCase() === low) ?? cleaned;
  }

  // Alias lookup
  if (low in DEGREE_ALIAS) {
    return DEGREE_ALIAS[low];
  }

  return '';
}

/**
 * Split a raw crawl license_type string into separate credential and degree
 * arrays.  The crawl often packs multiple tokens into a single field (e.g.
 * "PhD, LCSW").  This mirrors backend _extract_credentials_and_degrees.
 */
export function extractCredentialsAndDegrees(
  raw: string | null,
  licenseTypes: string[],
  degreeTypes: string[],
): { credentials: string[]; degrees: string[] } {
  if (!raw) return { credentials: [], degrees: [] };

  const tokens = raw.split(/[,/]+/);
  const credentials: string[] = [];
  const degrees: string[] = [];
  const seenCred = new Set<string>();
  const seenDeg = new Set<string>();

  for (const token of tokens) {
    const trimmed = token.trim();
    if (!trimmed) continue;

    const deg = normaliseDegree(trimmed, degreeTypes);
    if (deg && !seenDeg.has(deg)) {
      degrees.push(deg);
      seenDeg.add(deg);
    }

    const cred = normaliseLicense(trimmed, licenseTypes);
    if (cred && !seenCred.has(cred)) {
      credentials.push(cred);
      seenCred.add(cred);
    }
  }

  return { credentials, degrees };
}

/**
 * Normalise all staff members' role, license_type, and degrees fields
 * from crawl text to system values.
 *
 * When a staff member has no per-staff specialties but the clinic has
 * clinic-level specialties, auto-populate the staff member's specialties
 * from the clinic list so they aren't blank by default.
 *
 * Returns a new array with normalised values (does not mutate input).
 */
export function normaliseStaffFromCrawl(
  staff: CrawledStaffMember[],
  roleOptions: RoleOption[],
  licenseTypes: string[],
  degreeTypes: string[],
  clinicSpecialties: string[] = [],
): CrawledStaffMember[] {
  return staff.map((s) => {
    // If the crawl already provided arrays, keep them; otherwise parse
    // the raw string into credentials + degrees.
    const rawLicense = Array.isArray(s.license_type)
      ? null // already normalised
      : (s.license_type as unknown as string | null);

    const { credentials, degrees } = rawLicense
      ? extractCredentialsAndDegrees(rawLicense, licenseTypes, degreeTypes)
      : { credentials: Array.isArray(s.license_type) ? [...s.license_type] : [], degrees: [...(s.degrees ?? [])] };

    // Also parse the title field (e.g. "PsyD, LCP") for any credentials
    // or degrees that the crawl packed there instead of in license_type.
    // Only do this when license_type was a raw string (initial crawl), not
    // when it's already an array (user has already curated the values).
    if (!Array.isArray(s.license_type) && s.title) {
      const fromTitle = extractCredentialsAndDegrees(s.title, licenseTypes, degreeTypes);
      const credSet = new Set(credentials.map((c) => c.toLowerCase()));
      const degSet = new Set(degrees.map((d) => d.toLowerCase()));
      for (const c of fromTitle.credentials) {
        if (!credSet.has(c.toLowerCase())) {
          credentials.push(c);
          credSet.add(c.toLowerCase());
        }
      }
      for (const d of fromTitle.degrees) {
        if (!degSet.has(d.toLowerCase())) {
          degrees.push(d);
          degSet.add(d.toLowerCase());
        }
      }
    }

    // Auto-populate specialties from clinic-level list when empty.
    // The AI may return null/undefined instead of [] for specialties.
    const staffSpecs = Array.isArray(s.specialties) ? s.specialties : [];
    const specialties = staffSpecs.length > 0
      ? [...staffSpecs]
      : [...clinicSpecialties];

    return {
      ...s,
      specialties,
      role: inferRole(s, roleOptions),
      license_type: credentials,
      degrees,
    };
  });
}
